import React, { useReducer, useCallback, useMemo, useEffect } from 'react';
import { toast } from 'components/Shared/Toast';
import { useTranslation } from 'react-i18next';
import ContractDetail from 'components/ContractDetail/ContractDetail'
import CreateDocumentPanel from 'components/ContractList/CreateContract/index';
import CreateReferenceModal from 'components/Modals/CreateReference';
import DuplicateContractModal from 'components/Modals/DuplicateContract';
import RenewContractModalContainer from 'components/Modals/RenewContract';
import MoveToFolderModal from 'components/Modals/MoveToFolder';
import { useLocation, useParams } from 'react-router-dom';
import { fetcher } from 'api/client';
import { fetchContractList } from 'api/actions/contracts'
import { fetchFolders, fetchFolder } from 'api/actions/folders'
import { fetchRecord } from 'api/actions/records'
import { useQuery, useQueryClient } from 'react-query';
import produce from 'immer';
const ContractListStateContext = React.createContext();
const ContractListDispatchContext = React.createContext();

export const OPEN_CREATE_CONTRACT = 'OPEN_CREATE_CONTRACT';
export const CLOSE_CREATE_CONTRACT = 'CLOSE_CREATE_CONTRACT';
export const SET_CONTRACT_LIST = 'SET_CONTRACT_LIST';
export const SET_FOLDER_LIST = 'SET_FOLDER_LIST';
export const UPDATE_CONTRACT = 'UPDATE_CONTRACT';
export const SET_STATUS = 'SET_STATUS';
export const ADD_CONTRACT = 'ADD_CONTRACT';
export const DELETE_CONTRACT = 'DELETE_CONTRACT';
export const NEXT_PAGE = 'NEXT_PAGE';
export const PREV_PAGE = 'PREV_PAGE';
export const SET_CURRENT_CONTRACT = 'SET_CURRENT_CONTRACT';
export const UPDATE_CURRENT_CONTRACT = 'UPDATE_CURRENT_CONTRACT';
export const OPEN_CONTRACT_DETAIL = 'OPEN_CONTRACT_DETAIL';
export const CLOSE_CONTRACT_DETAIL = 'CLOSE_CONTRACT_DETAIL';
export const TOGGLE_CONTRACT_DETAIL = 'TOGGLE_CONTRACT_DETAIL';
export const SET_MODAL = 'SET_MODAL';
export const SET_META = 'SET_META';
export const SET_CURRENT_FOLDER = 'SET_CURRENT_FOLDER';
export const SET_CURRENT_RECORD = 'SET_CURRENT_RECORD';
export const SET_SELECTED_CONTRACTS = 'SET_SELECTED_CONTRACTS';
export const SELECT_ALL_CONTRACTS = 'SELECT_ALL_CONTRACTS';
export const UPDATE_FOLDER = 'UPDATE_FOLDER';

const reducer = produce((draft, action) => {
  // eslint-disable-next-line default-case
  switch (action.type) {
    case SET_STATUS:
      draft.status = action.status
      break;
    case UPDATE_CONTRACT:
      const contractId = action.contract.isChild ? action.contract.parentReference : action.contract._id;

      draft.contracts = draft.contracts.map(contract => {
        if (contract._id === contractId) {
          if (action.contract.isChild) {
            return {
              ...contract,
              childs: contract.childs.map(childContract => {
                if (childContract._id === action.contract._id) {
                  return {
                    ...childContract,
                    ...action.contract
                  };
                }

                return childContract
              })
            }
          }

          return {
            ...contract,
            ...action.contract
          };
        }

        return contract;
      })

      break;
    case SET_CONTRACT_LIST:

      draft.status = 'loaded';
      draft.contracts = action.contracts;
      draft.meta = {
        ...draft.meta,
        ...action.meta
      }
      break;
    case SET_FOLDER_LIST:
      draft.folders = action.folders;
      break;
    case NEXT_PAGE:
      if (draft.meta.page >= draft.meta.total) return draft;
      draft.meta = {
        ...draft.meta,
        page: draft.meta.page + 1,
      }
      break;
    case PREV_PAGE:
      if (draft.meta.page < 1) return draft;
      draft.meta = {
        ...draft.meta,
        page: Math.max(draft.meta.page - 1, 1),
      }
      break;
    case DELETE_CONTRACT:

      draft.templates = draft?.templates.filter((template) => template.templateId !== action?.templateId);
      draft.meta = {
        ...draft.meta,
        total: draft.meta.total - 1
      }

      break;
    case SET_CURRENT_CONTRACT:
      draft.currentContract = action.contract;
      break;
    case UPDATE_CURRENT_CONTRACT:
      draft.currentContract = {
        ...draft.contract,
        ...action.contract,
      }
      break;
    case OPEN_CREATE_CONTRACT:
      draft.isNewContractOpen = true;
      draft.referenceId = action.referenceId;
      draft.createDocumentOptions = action.options;
      break;
    case CLOSE_CREATE_CONTRACT:
      draft.isNewContractOpen = false;
      draft.createDocumentOptions = {};
      break;
    case OPEN_CONTRACT_DETAIL:
      draft.isContractDetailOpen = true;
      break
    case CLOSE_CONTRACT_DETAIL:
      draft.isContractDetailOpen = false;
      break;
    case TOGGLE_CONTRACT_DETAIL:
      draft.isContractDetailOpen = !draft.isContractDetailOpen;
      draft.currentContract = {}
      break;
    case SET_META:
      if (draft.meta.sortBy === action.meta.sortBy) draft.meta.sortDirection === '1' ? draft.meta.sortDirection = '-1' : draft.meta.sortDirection = '1';
      if (draft.meta.url !== '/contracts/all') draft.folders = [];
      if (action.meta.url === '/contracts/all' && !action.ignoreFolder) {
        action.meta.url = `${action.meta.url}${draft.meta.childOf && !draft.meta.childOf.includes('n=') ? `/${draft.meta.childOf}` : ''}`
      }
      draft.meta = {
        ...draft.meta,
        ...action.meta
      }
      break;
    case SET_CURRENT_FOLDER:
      draft.currentFolder = action.folder;
      draft.meta = {
        ...draft.meta,
        childOf: action.childOf
      }
      draft.folders = [];
      break;
    case SET_CURRENT_RECORD:
      draft.currentRecord = action.record;
      draft.meta = {
        ...draft.meta,
        recordId: action.record._id
      }
      break;
    case SET_SELECTED_CONTRACTS:
      draft.selectedContracts = action.selected
      break;
    case SELECT_ALL_CONTRACTS:
      draft.selectedContracts = draft.contracts.map(c => c._id)
      break;
    case SET_MODAL:
      const {name, ...modalProps} = action;
      draft.modalToOpen = action.name
      draft.modalProps = modalProps || {}
      break;
    case UPDATE_FOLDER:

      draft.folders = draft.folders.map(folder => {
        if (folder._id === action.folder._id) {
          return {
            ...folder,
            ...action.folder
          };
        }

        return folder;
      })
      break;
  }
});

const initialState = {
  contracts: [],
  status: 'loading',
  currentContract: {},
  isNewContractOpen: false,
  isContractDetailOpen: false,
  modalToOpen: '',
  modalProps: {},
  folders: [],
  currentFolder: {},
  currentRecord: {},
  selectedContracts: [],
  meta: {
    search: 'name|',
    sortBy: 'updatedAt',
    sortDirection: '-1',
    perPage: 10,
    limit: 10,
    page: 1,
    total: 5,
    url: '/contracts/all',
    listOnly: true,
    filter: 'active',
    recordId: '',
    searchTerm: '',
    pendingByMe: true,
    miniDashboardFilter: ''
  }
}

function ContractListProvider({ children }) {

  const { pathname } = useLocation();

  const { childOf, recordId, searchTerm } = useParams();

  const [state, dispatch] = useReducer(reducer, initialState);

  const toggleContractDetail = useCallback(() => {
    dispatch({ type: TOGGLE_CONTRACT_DETAIL })
  }, [])

  const closeModal = useCallback(() => {
    dispatch({ type: SET_MODAL, name: '' })
  }, [])

  const handleCloseCreate = useCallback(() => {
    dispatch({ type: CLOSE_CREATE_CONTRACT })
  }, [])

  useEffect(() => {
    let url = '/contracts/all';

    const listOnly = pathname === '/incoming-contracts' || !!searchTerm;

    if (listOnly && !recordId) {
      url = '/incoming-contracts'
    }
    if (url === '/contracts/all') {
      url = `${url}${childOf && !childOf.includes('n=') ? `/${childOf}` : ''}`
    }
    if (searchTerm) {
      url = `/dashboard/results/${searchTerm}`;
    }

    dispatch({
      type: SET_META, meta: {
        childOf,
        listOnly,
        recordId,
        url,
        searchTerm,
        enableFolders: url === '/contracts/all'
      },
      ignoreFolder: true,
    })
  }, [childOf, recordId, searchTerm, pathname])

  const queryClient = useQueryClient();

  const query = useCallback(async ({ refreshDashboard, refreshFolders } = {}) => {
    if (refreshDashboard) queryClient.invalidateQueries('minidashboards-fetch');
    if (refreshFolders) queryClient.invalidateQueries('fetch-folder-list');
    queryClient.invalidateQueries('fetch-contract-list');
  }, [queryClient])


  const queryParams = {
    page: state.meta.page,
    pendingByMe: state.meta.pendingByMe,
    perPage: 10,
    filter: state.meta.filter,
    search: state.meta.search,
    limit: state.meta.limit,
    url: state.meta.url,
    recordId: state.meta.recordId,
    sortBy: state.meta.sortBy,
    sortDirection: state.meta.sortDirection,
  }

  const { isLoading: isLoadingContracts } = useQuery(['fetch-contract-list', queryParams], () => fetcher(fetchContractList(queryParams)), {
    onSuccess: (data) => {
      dispatch({
        type: SET_CONTRACT_LIST,
        contracts: data.docs,
        meta: {
          page: Number(data.page),
          total: Number(data.pages),
        }
      })
    },
    enabled: !!state.meta.url
  });

  const { isLoading: isLoadingFolders } = useQuery(['fetch-folder-list'], () => fetcher(fetchFolders()), {
    onSuccess: (data) => {
      dispatch({
        type: SET_FOLDER_LIST,
        folders: data ?? [],
      })
    },
    enabled: state.meta.enableFolders
  });

  useQuery('fetch-folder', () => fetcher(fetchFolder(childOf)), {
    enabled: !!childOf,
    onSuccess: (data) => {
      dispatch({ type: SET_CURRENT_FOLDER, folder: data, childOf });
    }
  })

  useQuery('fetch-record', () => fetcher(fetchRecord(recordId)), {
    enabled: !!recordId,
    onSuccess: (data) => {
      dispatch({ type: SET_CURRENT_RECORD, record: data });
    }
  })
  const handleMoveToFolderSubmit = () => {
    query(); 
    dispatch({ type: SET_SELECTED_CONTRACTS, selected: [] });
  }

  const handleOpenCreateContract = ({referenceId, options}) => dispatch({ type: OPEN_CREATE_CONTRACT, referenceId, options })

  return (
    <ContractListStateContext.Provider value={{ ...state, query, isLoadingContracts, isLoadingFolders }}>
      <ContractListDispatchContext.Provider value={dispatch}>
        {children}
        <ContractDetail isOpen={state.isContractDetailOpen} toggle={toggleContractDetail} initialContract={state.currentContract} onRefreshList={query} />
        <CreateDocumentPanel isOpen={state.isNewContractOpen} referenceId={state.referenceId} options={state.createDocumentOptions} handleClose={handleCloseCreate} />
        <CreateReferenceModal isOpen={state.modalToOpen === 'SET_REFERENCE'} onSave={query} toggle={closeModal} contract={state.currentContract}  />
        <DuplicateContractModal isOpen={state.modalToOpen === 'DUPLICATE'} onSave={query} toggle={closeModal} contract={state.currentContract} />
        <RenewContractModalContainer isOpen={state.modalToOpen === 'RENEW'} onSave={query} toggle={closeModal} contract={state.currentContract} onOpenCreateContract={handleOpenCreateContract} />
        <MoveToFolderModal isOpen={state.modalToOpen === 'MOVE_TO'} onSubmit={handleMoveToFolderSubmit} toggle={closeModal} contract={state.currentContract} selectedContracts={state.selectedContracts} {...state.modalProps}  />
      </ContractListDispatchContext.Provider>
    </ContractListStateContext.Provider>
  )
}

function useContractListState() {
  const context = React.useContext(ContractListStateContext)
  if (context === undefined) {
    throw new Error('useContractListState must be used within a ContractListProvider')
  }
  return context
}

function useContractListDispatch() {
  const context = React.useContext(ContractListDispatchContext)
  if (context === undefined) {
    throw new Error('useContractListDispatch must be used within a ContractListProvider')
  }
  return context
}

function useContractList() {
  return [useContractListState(), useContractListDispatch()]
}

export { ContractListProvider, useContractList, useContractListState, useContractListDispatch }